import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import IconStarEmpty from '@adv-mt/mt-svg-icons/lib/StarEmpty.js'
import IconStarFilled from '@adv-mt/mt-svg-icons/lib/StarFilled.js'
import IconStarIntermediate from '@adv-mt/mt-svg-icons/lib/StarIntermediate.js'
import {Badge, BADGE_DESIGNS, BADGE_INTENTS} from '@adv-mt/ui/badge'
import {Rating, RATING_ICON_VARIANTS} from '@adv-mt/ui/rating'
import {Body1, Body2, Caption} from '@adv-mt/ui/typography'

import {BASE_CLASS as PAGE_BASE_CLASS} from '../../config.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-dealerRatingsReview`

const DATE_FORMAT = {day: '2-digit', month: '2-digit', year: 'numeric'}

const renderStar = variant => {
  switch (variant) {
    case RATING_ICON_VARIANTS.FULL:
      return <IconStarFilled size={atomIconSizes.small} />
    case RATING_ICON_VARIANTS.HALF:
      return <IconStarIntermediate size={atomIconSizes.small} />
    case RATING_ICON_VARIANTS.EMPTY:
    default:
      return <IconStarEmpty size={atomIconSizes.small} />
  }
}

const ReviewStars = ({score}) => {
  const {i18n} = useSuiContext()

  const rating = Math.round(score * 2) / 2

  return (
    <Rating
      aria-label={i18n.t('DEALER_RATINGS.RATING_LABEL', {value: i18n.n(rating)})}
      className={`${BASE_CLASS}-stars`}
      getValueLabel={starValue => i18n.t('DEALER_RATINGS.STAR_LABEL', {smart_count: starValue})}
      renderIcon={renderStar}
      value={score}
      allowHalf
      readOnly
      inert=""
    />
  )
}

ReviewStars.displayName = 'ReviewStars'

ReviewStars.propTypes = {
  score: PropTypes.number
}

const DealerRatingsReview = ({review, isBordered = false, showResponse = false}) => {
  const {domain, i18n} = useSuiContext()

  const config = domain.get('config')

  const {givenAt, name, reply, score, tags = [], textReview} = review

  const dateFormatter = new Intl.DateTimeFormat(config.get('DEFAULT_CULTURE'), DATE_FORMAT)
  const formattedDate = dateFormatter.format(new Date(givenAt))

  const hasReply = showResponse && Boolean(reply?.text)

  return (
    <article className={cx(BASE_CLASS, isBordered && `${BASE_CLASS}--bordered`)}>
      {isBordered ? (
        <div className={`${BASE_CLASS}-titleBar`}>
          <ReviewStars score={score} />

          <Body1 className={`${BASE_CLASS}-name`} highlight>
            {name}
          </Body1>

          <Caption className={`${BASE_CLASS}-date`} highlight>
            {formattedDate}
          </Caption>
        </div>
      ) : (
        <div className={`${BASE_CLASS}-header`}>
          <Body1 highlight>{name}</Body1>

          <div className={`${BASE_CLASS}-meta`}>
            <ReviewStars score={score} />

            <Body2 aria-hidden="true">·</Body2>

            <Body2>{formattedDate}</Body2>
          </div>
        </div>
      )}

      <div className={`${BASE_CLASS}-content`}>
        {Boolean(textReview) && (isBordered ? <Body2>{textReview}</Body2> : <Body1>{textReview}</Body1>)}

        {tags.length > 0 && (
          <div className={`${BASE_CLASS}-tags`}>
            {tags.map(tag => (
              <Badge design={BADGE_DESIGNS.SOFT} intent={BADGE_INTENTS.NEUTRAL} key={tag}>
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {hasReply && (
        <div className={`${BASE_CLASS}-reply`}>
          <div className={`${BASE_CLASS}-replyHeader`}>
            {reply.authorName ? (
              <Body2 highlight>{i18n.t('DEALER_RATINGS.REPLY_TITLE', {name: reply.authorName})}</Body2>
            ) : (
              <Body2 highlight>{i18n.t('DEALER_RATINGS.REPLY_TITLE_FALLBACK')}</Body2>
            )}

            {Boolean(reply.givenAt) && <Body2>{dateFormatter.format(new Date(reply.givenAt))}</Body2>}
          </div>

          <Body2>{reply.text}</Body2>
        </div>
      )}
    </article>
  )
}

DealerRatingsReview.displayName = 'DealerRatingsReview'

DealerRatingsReview.propTypes = {
  review: PropTypes.shape({
    feedbackId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    givenAt: PropTypes.string,
    name: PropTypes.string,
    reply: PropTypes.shape({
      authorName: PropTypes.string,
      givenAt: PropTypes.string,
      text: PropTypes.string
    }),
    score: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    textReview: PropTypes.string
  }).isRequired,
  isBordered: PropTypes.bool,
  showResponse: PropTypes.bool
}

export default DealerRatingsReview
